import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import authApi from '@/services/auth';
import Button from '@/components/ui/Button';
import Alert from '@/components/ui/Alert';
import Spinner from '@/components/ui/Spinner';
import { validateRedirectUrl } from '@/utils/redirect';

type VerificationStatus = 'verifying' | 'success' | 'error' | 'expired';

/**
 * Verify Email Page
 * Handles email verification from the link sent to user's email
 *
 * WP-BUG-35: Implements idempotency guard and AbortController to prevent
 * race conditions from duplicate requests (e.g., email link scanners,
 * double-clicks, or React re-renders triggering multiple API calls).
 *
 * WP-SEC-27: when the response carries passwordSetupToken (the account has
 * no password after verification), the page continues to "Set your password"
 * (ResetPasswordPage with ?setup=1) instead of showing the success screen.
 */
export default function VerifyEmailPage() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [status, setStatus] = useState<VerificationStatus>('verifying');
  const [errorMessage, setErrorMessage] = useState('');
  const [email, setEmail] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  // WP-BUG-35: Idempotency guard to prevent duplicate verification attempts
  // Using useRef instead of useState to avoid stale closure issues and
  // to persist across re-renders without causing additional renders
  const hasAttemptedVerification = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Get email from query params for resend functionality
  const emailFromQuery = searchParams.get('email');

  const verifyEmail = useCallback(async () => {
    // WP-BUG-35: Guard against duplicate verification attempts
    // This prevents race conditions from:
    // 1. Email link scanners/prefetch (first request might fail)
    // 2. React double-renders (StrictMode in dev, edge cases in prod)
    // 3. User double-clicking verification link
    if (hasAttemptedVerification.current) {
      return;
    }

    if (!token) {
      setStatus('error');
      setErrorMessage('Invalid verification link.');
      return;
    }

    // Mark as attempted before making the request
    hasAttemptedVerification.current = true;

    // Cancel any previous in-flight request (defensive)
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const result = await authApi.verifyEmail(token, { signal: controller.signal });
      // WP-SEC-27: verifying an unverified account clears any password set
      // before the proof. Continue straight to "Set your password" with the
      // one-time token, keeping a pending post-sign-up redirect (invitations).
      if (result.passwordSetupToken) {
        const params = new URLSearchParams({ setup: '1' });
        const storedRedirect = validateRedirectUrl(localStorage.getItem('postAuthRedirect'));
        localStorage.removeItem('postAuthRedirect');
        if (storedRedirect) {
          params.set('redirect', storedRedirect);
        }
        navigate(
          `/reset-password/${encodeURIComponent(result.passwordSetupToken)}?${params.toString()}`,
          { replace: true }
        );
        return;
      }
      setStatus('success');
    } catch (err) {
      // Ignore aborted requests (component unmounted or new request started)
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      // Also check for axios cancel error
      if (err instanceof Error && err.message === 'canceled') {
        return;
      }

      if (err instanceof Error) {
        const errorMsg = err.message.toLowerCase();

        // WP-BUG-35: Treat "already verified" as success
        // This handles the case where a previous request succeeded but
        // the user sees an error from a race condition
        if (errorMsg.includes('already verified') ||
            errorMsg.includes('already been verified')) {
          setStatus('success');
          return;
        }

        // Check for expired token
        if (errorMsg.includes('expired')) {
          setStatus('expired');
          setErrorMessage('This verification link has expired.');
          return;
        }

        // WP-BUG-35: Handle "invalid token" gracefully
        // If the error is "invalid or expired" and we just tried to verify,
        // it might be because a concurrent request already consumed the token.
        // Show a slightly more helpful message.
        if (errorMsg.includes('invalid') && errorMsg.includes('token')) {
          // The token might have been consumed by a concurrent request
          // (e.g., email scanner or double-click). Check if we should
          // show success instead of error by prompting user to try logging in.
          setStatus('error');
          setErrorMessage(
            'This verification link is no longer valid. ' +
            'If you just clicked this link, your email may already be verified. ' +
            'Please try signing in.'
          );
          return;
        }

        // Generic error
        setStatus('error');
        setErrorMessage(err.message);
      } else {
        setStatus('error');
        setErrorMessage('Failed to verify email. Please try again.');
      }
    }
  }, [token, navigate]);

  useEffect(() => {
    if (emailFromQuery) {
      setEmail(emailFromQuery);
    }
    verifyEmail();

    // WP-BUG-35: Cleanup function to abort request on unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [verifyEmail, emailFromQuery]);

  const handleResendVerification = async () => {
    if (!email) {
      setErrorMessage('Please provide your email address to resend verification.');
      return;
    }

    setIsResending(true);
    setResendSuccess(false);

    try {
      await authApi.resendVerificationEmail(email);
      setResendSuccess(true);
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to resend verification email.');
    } finally {
      setIsResending(false);
    }
  };

  // Verifying state
  if (status === 'verifying') {
    return (
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <Spinner size="lg" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Verifying your email</h2>
        <p className="text-gray-600">Please wait while we verify your email address...</p>
      </div>
    );
  }

  // Handle navigation after successful verification
  const handleSuccessNavigation = () => {
    const storedRedirect = localStorage.getItem('postAuthRedirect');
    localStorage.removeItem('postAuthRedirect');
    // Validate stored redirect (defense in depth against open redirect)
    const validatedRedirect = validateRedirectUrl(storedRedirect);
    if (validatedRedirect) {
      // Preserve the redirect in the login URL so user ends up at the right place after login
      navigate(`/login?email_verified=true&redirect=${encodeURIComponent(validatedRedirect)}`, { replace: true });
    } else {
      navigate('/login?email_verified=true', { replace: true });
    }
  };

  // Success state
  if (status === 'success') {
    return (
      <div className="text-center">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
          <svg
            className="h-6 w-6 text-green-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Email Verified!</h2>
        <p className="text-gray-600 mb-6">
          Your email has been verified successfully. You can now sign in to your account.
        </p>
        <Button className="w-full" onClick={handleSuccessNavigation}>
          Sign in to your account
        </Button>
      </div>
    );
  }

  // Expired state
  if (status === 'expired') {
    return (
      <div className="text-center">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 mb-4">
          <svg
            className="h-6 w-6 text-yellow-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Link Expired</h2>
        <p className="text-gray-600 mb-6">{errorMessage}</p>

        {resendSuccess ? (
          <Alert variant="success" className="mb-6">
            A new verification email has been sent. Please check your inbox.
          </Alert>
        ) : (
          <div className="space-y-4">
            {email ? (
              <Button
                className="w-full"
                onClick={handleResendVerification}
                isLoading={isResending}
              >
                Resend verification email
              </Button>
            ) : (
              <>
                <p className="text-sm text-gray-500 mb-2">
                  Enter your email to receive a new verification link:
                </p>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                <Button
                  className="w-full mt-2"
                  onClick={handleResendVerification}
                  isLoading={isResending}
                  disabled={!email}
                >
                  Send new verification email
                </Button>
              </>
            )}
          </div>
        )}

        <p className="mt-6 text-center text-sm text-gray-600">
          <Link
            to="/login"
            className="font-medium text-primary-600 hover:text-primary-500"
          >
            Back to sign in
          </Link>
        </p>
      </div>
    );
  }

  // Error state
  return (
    <div className="text-center">
      <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
        <svg
          className="h-6 w-6 text-red-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Verification Failed</h2>
      <p className="text-gray-600 mb-6">{errorMessage}</p>

      {resendSuccess ? (
        <Alert variant="success" className="mb-6">
          A new verification email has been sent. Please check your inbox.
        </Alert>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-gray-500 mb-2">
            Enter your email to receive a new verification link:
          </p>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          <Button
            className="w-full"
            onClick={handleResendVerification}
            isLoading={isResending}
            disabled={!email}
          >
            Send new verification email
          </Button>
        </div>
      )}

      <p className="mt-6 text-center text-sm text-gray-600">
        <Link
          to="/login"
          className="font-medium text-primary-600 hover:text-primary-500"
        >
          Back to sign in
        </Link>
      </p>
    </div>
  );
}
